import { Dispatch, SetStateAction } from 'react';
import { AssistantListItemSelectionStateProps } from '../hooks/useAssistantListItemSelectionHandler';

export enum CalculateIndexAction {
    Increment = 'Increment',
    Decrement = 'Decrement',
}

interface AssistantKeyboardNavigationHelperProps {
    element: HTMLElement;
    event: KeyboardEvent;
    focusableElements: HTMLElement[];
    setListItemSelectionState: Dispatch<SetStateAction<AssistantListItemSelectionStateProps>>;
    action: CalculateIndexAction;
    resetTabIndex: () => void;
    focusSearchInput?: () => void;
}

const getCalculatedTabIndex = (
    action: CalculateIndexAction,
    focusableElementIndex: number,
    focusableElements: HTMLElement[]
) => {
    let calculatedIndex: number;
    if (action === CalculateIndexAction.Increment) {
        calculatedIndex =
            focusableElementIndex + 1 >= focusableElements.length
                ? focusableElements.length
                : focusableElementIndex + 1;
    } else {
        calculatedIndex = focusableElementIndex - 1;
    }
    return calculatedIndex;
};

export const handleAssistantSearchListTabKey = ({
    focusableElements,
    element,
    setListItemSelectionState,
    action,
    event,
}: Omit<AssistantKeyboardNavigationHelperProps, 'resetTabIndex'>) => {
    let handled = false;
    const listItemElements = focusableElements.filter((el) => el.className.includes('drawer-list-item'));
    const drawerListItem = element.className.includes('drawer-list-item');
    const focusableItemIndex = focusableElements.findIndex((el) => el === element);

    if (drawerListItem || focusableItemIndex > 0) {
        const calculatedIndex = getCalculatedTabIndex(action, focusableItemIndex, focusableElements);
        handled = calculatedIndex >= 0 && calculatedIndex < focusableElements.length;
        setListItemSelectionState((prev) => {
            let selectedListItemIndex = prev.selectedIndex;
            if (calculatedIndex === focusableElements.length) {
                selectedListItemIndex = listItemElements.length;
            } else if (calculatedIndex === 0) {
                selectedListItemIndex = 0;
            }
            return {
                ...prev,
                selectedTabIndex: calculatedIndex > 0 ? calculatedIndex : 0,
                selectedIndex: selectedListItemIndex,
                isTabNavigation: calculatedIndex > 0 && calculatedIndex < focusableElements.length,
                openSummary: false,
            };
        });
    } else {
        setListItemSelectionState((prev) => {
            const drawerListItemElement = listItemElements.find((_el, index) => index === prev.selectedIndex - 1);
            const focusableElementIndex = drawerListItemElement
                ? focusableElements.findIndex((el) => el === drawerListItemElement)
                : 0;
            const calculatedIndex = getCalculatedTabIndex(action, focusableElementIndex, focusableElements);
            handled = calculatedIndex >= 0 && calculatedIndex < focusableElements.length;
            return {
                ...prev,
                selectedTabIndex: calculatedIndex > 0 ? calculatedIndex : 0,
                selectedIndex: calculatedIndex > 0 ? prev.selectedIndex : 0,
                isTabNavigation: calculatedIndex > 0 && calculatedIndex < focusableElements.length,
                openSummary: false,
            };
        });
    }

    if (handled) {
        event.stopPropagation();
        event.preventDefault();
    }
};

export const handleAssistantSearchListArrowKey = ({
    element,
    event,
    setListItemSelectionState,
    action,
    focusableElements,
    focusSearchInput,
    resetTabIndex,
}: AssistantKeyboardNavigationHelperProps) => {
    const listItemElements = focusableElements.filter((el) => el.className.includes('drawer-list-item'));
    const drawerListItemIndex = listItemElements.findIndex((el) => el === element);
    const focusableItemIndex = focusableElements.findIndex((el) => el === element);
    let calculatedIndex = 0;
    if (drawerListItemIndex >= 0) {
        if (action === CalculateIndexAction.Increment) {
            calculatedIndex = drawerListItemIndex + 1 > listItemElements.length ? 1 : drawerListItemIndex + 1;
        } else {
            calculatedIndex = drawerListItemIndex - 1 < 1 ? listItemElements.length : drawerListItemIndex - 1;
        }
        calculatedIndex++;
        if (focusSearchInput) {
            resetTabIndex();
            focusSearchInput();
        }
        setListItemSelectionState((prev) => ({
            ...prev,
            selectedIndex: calculatedIndex,
            isTabNavigation: false,
            openSummary: false,
        }));
    } else if (focusableItemIndex > 0) {
        if (action === CalculateIndexAction.Increment) {
            const foundNextListItem = focusableElements.find(
                (el, index) => index > focusableItemIndex && el.className.includes('drawer-list-item')
            );
            if (foundNextListItem) {
                const foundNextListItemIndex = listItemElements.findIndex((el) => el === foundNextListItem);
                calculatedIndex = foundNextListItemIndex + 1 > listItemElements.length ? 1 : foundNextListItemIndex + 1;
            } else {
                calculatedIndex = 1;
            }
        } else {
            const foundPrevListItem = focusableElements.findLast(
                (el, index) => index < focusableItemIndex && el.className.includes('drawer-list-item')
            );
            if (foundPrevListItem) {
                const foundPrevListItemIndex = listItemElements.findIndex((el) => el === foundPrevListItem);
                calculatedIndex = foundPrevListItemIndex - 1 < 1 ? listItemElements.length : foundPrevListItemIndex;
            } else {
                calculatedIndex = listItemElements.length;
            }
        }
        if (focusSearchInput) {
            resetTabIndex();
            focusSearchInput();
        }
        setListItemSelectionState((prev) => ({
            ...prev,
            selectedIndex: calculatedIndex,
            isTabNavigation: false,
            openSummary: false,
        }));
    } else {
        /** event triggered from the search Input */
        setListItemSelectionState((prev) => {
            if (action === CalculateIndexAction.Increment) {
                calculatedIndex = prev.selectedIndex + 1 > listItemElements.length ? 1 : prev.selectedIndex + 1;
            } else {
                calculatedIndex = prev.selectedIndex - 1 < 1 ? listItemElements.length : prev.selectedIndex - 1;
            }
            return {
                ...prev,
                selectedIndex: calculatedIndex,
                isTabNavigation: false,
                openSummary: false,
            };
        });
    }

    event.preventDefault();
    event.stopPropagation();
};
